import React, { useState } from 'react';
import { hairColorLevels } from '@/data/hairColorChart';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { toast } from 'sonner';

const MAX_COLORS = 3;

const ColorChart = ({ onColorSelect, selectedColors = [], onCompare, loading }) => {
  const [manualCode, setManualCode] = useState('');
  const [expandedLevels, setExpandedLevels] = useState([1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);

  const toggleLevel = (level) => {
    setExpandedLevels(prev =>
      prev.includes(level) ? prev.filter(l => l !== level) : [...prev, level]
    );
  };

  const handleColorClick = (shade) => {
    const isSelected = selectedColors.some(c => c.code === shade.code);
    if (!isSelected && selectedColors.length >= MAX_COLORS) {
      toast.error(`You can select up to ${MAX_COLORS} colors at a time`);
      return;
    }
    onColorSelect(shade);
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!manualCode) return;

    if (selectedColors.length >= MAX_COLORS) {
      toast.error(`You can select up to ${MAX_COLORS} colors at a time`);
      return;
    }

    let foundShade = null;
    for (const level of hairColorLevels) {
      foundShade = level.shades.find(s => s.code === manualCode.toUpperCase());
      if (foundShade) break;
    }

    if (foundShade) {
      onColorSelect(foundShade);
    } else {
      onColorSelect({
        code: manualCode.toUpperCase(),
        name: `Custom ${manualCode.toUpperCase()}`,
        hex: '#666666'
      });
    }
    setManualCode('');
  };

  // Check if any selected colors belong to a given level
  const levelHasSelection = (level) => {
    return level.shades.some(s => selectedColors.some(c => c.code === s.code));
  };

  return (
    <div className="w-full" data-testid="color-chart">
      <Tabs defaultValue="chart" className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-bg-secondary">
          <TabsTrigger value="chart" data-testid="chart-tab">Color Chart</TabsTrigger>
          <TabsTrigger value="manual" data-testid="manual-tab">Enter Code</TabsTrigger>
        </TabsList>

        <TabsContent value="chart" className="mt-6" data-testid="chart-content">
          <div className="space-y-4">
            {hairColorLevels.map((level) => (
              <div key={level.level} className="border border-border-color rounded-sm overflow-hidden bg-bg-primary">
                <button
                  onClick={() => toggleLevel(level.level)}
                  className="w-full px-6 py-4 flex items-center justify-between bg-bg-secondary hover:bg-bg-tertiary transition-colors"
                  data-testid={`level-${level.level}-toggle`}
                >
                  <div className="flex items-center space-x-4">
                    <div
                      className="w-8 h-8 rounded-sm border border-border-color"
                      style={{ backgroundColor: level.baseColor }}
                    />
                    <div className="text-left">
                      <p className="font-serif font-medium text-text-primary">
                        Level {level.level}
                      </p>
                      <p className="font-sans text-sm text-text-secondary">
                        {level.name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {levelHasSelection(level) && (
                      <span className="bg-accent-primary text-white text-xs px-2 py-1 rounded-sm font-sans">
                        Selected
                      </span>
                    )}
                    {expandedLevels.includes(level.level) ? (
                      <ChevronUp className="h-5 w-5 text-text-muted" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-text-muted" />
                    )}
                  </div>
                </button>

                {expandedLevels.includes(level.level) && (
                  <div className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid={`level-${level.level}-shades`}>
                      {level.shades.map((shade) => {
                        const isSelected = selectedColors.some(c => c.code === shade.code);
                        const isDisabled = !isSelected && selectedColors.length >= MAX_COLORS;
                        return (
                          <button
                            key={shade.code}
                            onClick={() => handleColorClick(shade)}
                            disabled={isDisabled}
                            className={`group relative p-4 border rounded-sm transition-all ${
                              isSelected
                                ? 'border-accent-primary bg-accent-primary/5 shadow-md'
                                : isDisabled
                                  ? 'border-border-color bg-bg-primary opacity-40 cursor-not-allowed'
                                  : 'border-border-color bg-bg-primary hover:border-accent-primary hover:shadow-md'
                            }`}
                            data-testid={`shade-${shade.code}`}
                          >
                            <div
                              className="w-full h-16 rounded-sm border border-border-color mb-3"
                              style={{ backgroundColor: shade.hex }}
                            />
                            <p className="font-sans text-xs font-bold text-text-primary mb-1">
                              {shade.code}
                            </p>
                            <p className="font-sans text-xs text-text-secondary leading-tight">
                              {shade.name}
                            </p>
                            {isSelected && (
                              <div className="absolute top-2 right-2 w-6 h-6 bg-accent-primary text-white rounded-full flex items-center justify-center text-xs font-bold">
                                {selectedColors.findIndex(c => c.code === shade.code) + 1}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {/* Inline compare button when this level has selections */}
                    {levelHasSelection(level) && selectedColors.length >= 1 && (
                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={onCompare}
                          disabled={loading}
                          className="bg-accent-primary text-white px-6 py-3 rounded-sm font-sans text-sm tracking-wider uppercase hover:bg-accent-hover transition-colors disabled:opacity-50"
                          data-testid={`level-${level.level}-compare-btn`}
                        >
                          {loading ? 'Processing...' : `Compare ${selectedColors.length} Color${selectedColors.length > 1 ? 's' : ''}`}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="manual" className="mt-6" data-testid="manual-content">
          <form onSubmit={handleManualSubmit} className="space-y-4">
            <div>
              <Label htmlFor="color-code" className="font-sans text-text-primary">
                Enter Color Code
              </Label>
              <p className="font-sans text-sm text-text-muted mb-3">
                e.g., 5N, 7G, 8A, 6RR
              </p>
              <Input
                id="color-code"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="Enter code..."
                className="font-sans"
                data-testid="manual-color-input"
              />
            </div>
            <button
              type="submit"
              className="bg-text-primary text-bg-primary px-8 py-4 font-sans text-sm tracking-widest uppercase hover:bg-accent-primary transition-colors rounded-sm"
              data-testid="add-color-btn"
            >
              ADD COLOR
            </button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ColorChart;
